import React, {
  useState,
  useEffect,
} from 'react';
import { diff, detailedDiff, addedDiff } from '@tmap/mmm-core/deep-object-diff';
import deepMerge from '@tmap/mmm-core/deepMerge';
import deepFilter from '@tmap/mmm-core/deepFilter';
import { FormSpy } from './final-form/react';
import useDebounced from './useDebounced';

/** Return the new set of form values,
 * and whether or not they are different from the initialValues
 * Handles the logic relating to unset/undefined/null fields
 * Ignores values declared as invalid by the form
 */
export const processState = (formState) => {
  const { initialValues, values, errors } = formState;
  const { added, updated } = detailedDiff(
    // Unify unset/undefined/null to unset
    deepFilter(initialValues, (v) => typeof v !== 'undefined' && v !== null),
    // Only use valid form values
    addedDiff(errors, values),
  );
  // No fields will be deleted in the diff, they will be changed to undefined/null
  const newValues = deepMerge(
    // Remove undefined/null additions, they were already unset
    deepFilter(added, (v) => typeof v !== 'undefined' && v !== null),
    // Keep everything else
    updated,
  );
  const newState = deepMerge(initialValues, newValues);
  return [
    newState,
    Object.keys(diff(initialValues, newState)).length > 0,
  ];
};

function AutoSaveSpy(props) {
  const {
    onFormChange,
    saveDelay = 0,
  } = props;

  const [inflight, setInflight] = useState(false);
  const [formState, setFormState] = useState();

  useEffect(() => {
    setInflight(false);
  }, [formState?.initialValues]);

  const onChange = useDebounced((state) => {
    setFormState(state);
  }, saveDelay);

  useEffect(() => {
    if (!inflight && formState) {
      const [newState, different] = processState(formState);
      if (different) {
        setInflight(true);
        onFormChange(newState);
      }
    }
  }, [inflight, formState, onFormChange]);

  return (
    <FormSpy
      subscription={{ initialValues: true, values: true, errors: true }}
      onChange={onChange}
    />
  );
}

export default AutoSaveSpy;
